import { useState, useEffect } from 'react';
import { Lock, User, Check, Clock, Mail, Key, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [activity, setActivity] = useState([]);
  const [actLoading, setActLoading] = useState(true);

  useEffect(() => {
    api.get('/api/activity')
      .then(r => setActivity(r.data.activity || []))
      .finally(() => setActLoading(false));
  }, []);

  const updatePassword = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) return toast.error('New passwords do not match');
    if (form.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await api.post('/api/auth/update-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success('Password updated!');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally { setLoading(false); }
  };

  const actionIcons = {
    login: <Shield size={13} color="var(--blue)" />,
    register: <User size={13} color="var(--green)" />,
    password_change: <Key size={13} color="var(--amber)" />,
    campaign_launch: <Mail size={13} color="var(--blue)" />,
  };

  const planBadge = user?.plan === 'pro'
    ? <span className="badge badge-amber">⭐ Pro</span>
    : <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, padding:'2px 8px', borderRadius:20, background:'var(--blue-dim)', color:'#93c5fd', fontWeight:500 }}>Free · Everything unlocked during launch 🎉</span>;

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Settings</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Manage your account and preferences</p>
      </div>

      {/* Profile */}
      <div className="card" style={{ padding: 22, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <User size={15} color="var(--text2)" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Profile</span>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Full name</div>
            <div style={{ fontSize: 13, padding: '9px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', color: 'var(--text2)' }}>{user?.name}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Email</div>
            <div style={{ fontSize: 13, padding: '9px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', color: 'var(--text2)' }}>{user?.email}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Plan</div>
            {planBadge}
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="card" style={{ padding: 22, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Lock size={15} color="var(--text2)" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Change password</span>
        </div>
        <form onSubmit={updatePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Current password</label>
            <input className="input" type="password" placeholder="Enter current password" value={form.currentPassword} onChange={e => setForm({ ...form, currentPassword: e.target.value })} required />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>New password</label>
            <input className="input" type="password" placeholder="Min 6 characters" value={form.newPassword} onChange={e => setForm({ ...form, newPassword: e.target.value })} required minLength={6} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Confirm new password</label>
            <input className="input" type="password" placeholder="Repeat new password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ alignSelf: 'flex-start', minWidth: 160 }}>
            {done ? <><Check size={14} /> Updated!</> : loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>

      {/* Activity log */}
      <div className="card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={14} color="var(--text2)" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Activity log</span>
        </div>
        {actLoading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
        ) : activity.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No activity yet</div>
        ) : activity.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: i < activity.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {actionIcons[a.action] || <Clock size={13} color="var(--text3)" />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{a.description}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.action}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
              {new Date(a.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
